import { PlusOutlined } from '@ant-design/icons';
import { ScopeButton, ToggleButton } from '@monorepo/shared-lib';
import { EntityTableContext } from 'components/Entity/EntityTableContext';
import { StyledTooltip } from 'components/Custom/Tooltip';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AccountType } from 'store/api/enums';
import { AdvancedUserRightsCodes, LicenseType } from 'store/app/enums';
import { User } from 'store/auth/models';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { Entity, Organisation } from 'store/organisation/models';
import styled from 'styled-components';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { ToggleButtonAnchor } from 'utils/ToggleButtonAnchor';

const LargeScopeButton = styled(ScopeButton)`
  width: 105px;
  height: 30px;
  font-size: 12px;
  line-height: 12px;
  padding: 4px 5px;
  margin: 7px 13px;
`;

const SmallScopeButton = styled(ScopeButton)`
  width: 99px;
  height: 25px;
  font-size: 12px;
  line-height: 12px;
  padding: 4px 5px;
`;

interface AddReturnButtonProps {
  record: Entity | Organisation;
  onClick: () => void;
  toggleButton?: boolean;
  size?: 'small' | 'large';
}

const AddReturnButtonView: React.FC<
  AddReturnButtonProps & {
    currentUser?: User;
    hasAdvancedUserRights: boolean;
  }
> = ({
  record,
  onClick,
  toggleButton,
  size = 'small',
  currentUser,
  hasAdvancedUserRights,
}) => {
  const { t } = useTranslation();

  const shouldShowButton = () => {
    return (
      currentUser &&
      AccountType[currentUser?.accountType] !== AccountType.CLIENT &&
      (!hasAdvancedUserRights ||
        hasAdvancedUserRight(
          currentUser,
          record,
          AdvancedUserRightsCodes.CREATE_EDIT_RETURN
        ))
    );
  };

  const isDisabled = () => {
    return !record?.connectedProperly || !record?.hasTaxRates;
  };

  const getTooltip = () => {
    let message = '';

    if (!record?.connectedProperly) {
      message = t('organisation.entity-not-connected-description');
    } else if (!record?.hasTaxRates) {
      message = t('organisation.tax-rates-not-configured-tooltip');
    } else if (
      !currentUser ||
      (hasAdvancedUserRights &&
        !hasAdvancedUserRight(
          currentUser,
          record,
          AdvancedUserRightsCodes.CREATE_EDIT_RETURN
        ))
    ) {
      message = t('global.edit-return-rights-warning');
    } else {
      message = t('global.cannot-edit-return-submission-in-progress');
    }

    return message;
  };

  const StyledButton = size === 'large' ? LargeScopeButton : SmallScopeButton;

  const renderToggleButton = () => {
    return (
      <ToggleButtonAnchor>
        <ToggleButton
          type="primary"
          shape="circle"
          icon={<PlusOutlined />} //@ts-ignore
          paddinglft="94px"
          disabled={isDisabled()}
          style={
            isDisabled()
              ? {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#d9d9d9',
                  color: '#8c8c8c',
                  cursor: 'not-allowed',
                }
              : {}
          }
          onClick={!isDisabled() ? onClick : undefined}
        >
          <span className="show-on-hover">{t('organisation.add-return')}</span>
        </ToggleButton>
      </ToggleButtonAnchor>
    );
  };

  const renderScopeButton = () => {
    return (
      <StyledButton
        type={'primary'}
        onClick={!isDisabled() ? onClick : undefined} // @ts-ignore
        icon={<PlusOutlined width={'16px'} height={'16px'} />}
        disabled={isDisabled()}
      >
        <span>{t('organisation.add-return')}</span>
      </StyledButton>
    );
  };

  return (
    <>
      {shouldShowButton() && (
        <StyledTooltip title={isDisabled() ? getTooltip() : undefined}>
          {toggleButton ? renderToggleButton() : renderScopeButton()}
        </StyledTooltip>
      )}
    </>
  );
};

const AddReturnButtonConnected: React.FC<AddReturnButtonProps> = (props) => {
  const hasAdvancedUserRightsPre = useSelector(getBillingHasAdvancedRights);
  const currentUser = useSelector(getAuthUser);
  const userLicence = useSelector(getAuthUserLicence);
  const [hasAdvancedUserRights, setHasAdvancedUserRights] =
    useState<boolean>(false);

  useEffect(() => {
    setHasAdvancedUserRights(
      hasAdvancedUserRightsPre || userLicence?.licenseType === LicenseType.TRIAL
    );
  }, [hasAdvancedUserRightsPre, userLicence]);

  return (
    <AddReturnButtonView
      {...props}
      currentUser={currentUser}
      hasAdvancedUserRights={hasAdvancedUserRights}
    />
  );
};

export const AdddReturnButton: React.FC<AddReturnButtonProps> = (props) => {
  const table = useContext(EntityTableContext);
  if (table) {
    return (
      <AddReturnButtonView
        {...props}
        currentUser={table.currentUser}
        hasAdvancedUserRights={table.hasAdvancedUserRights}
      />
    );
  }
  return <AddReturnButtonConnected {...props} />;
};
